'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { X, MapPin, IndianRupee, FileText, CheckCircle2, Navigation, Mic, MicOff, ChevronDown } from 'lucide-react';
import { landRatesService } from '@/lib/services/landRatesService';

interface AddRateFormValues {
  locality: string;
  rate: string;
  landType: 'Registered' | 'Unregistered' | '';
  yourName: string;
}

interface AddRateBottomSheetProps {
  open: boolean;
  onClose: () => void;
  pinnedLat?: number | null;
  pinnedLng?: number | null;
  onSearchOnMap?: (localityQuery: string) => void;
  initialLocality?: string;
  initialRate?: string;
  initialLandType?: 'Registered' | 'Unregistered' | '';
}

const LAND_TYPES = [
  {
    value: 'Registered',
    label: 'Registered',
    description: 'Fully registered — safest ownership',
    badgeClass: 'marker-registry',
  },
  {
    value: 'Unregistered',
    label: 'Unregistered',
    description: 'Not registered — verify before buying',
    badgeClass: 'marker-gpa',
  },
] as const;

// Voice state
const VOICE_LANGUAGES = [
  { code: 'hi-IN', label: 'हिंदी', short: 'HI' },
  { code: 'en-IN', label: 'English', short: 'EN' },
  { code: 'bn-IN', label: 'বাংলা', short: 'BN' },
  { code: 'hi-IN-hinglish', label: 'Hinglish', short: 'HG', actualCode: 'hi-IN' },
] as const;

type VoiceLang = typeof VOICE_LANGUAGES[number];

// Extract numbers from spoken text (handles Indian number words)
function extractRate(text: string): string | null {
  // Try direct number first
  const directNum = text.match(/\d[\d,]*/);
  if (directNum) return directNum[0].replace(/,/g, '');

  // Hindi/Hinglish number words
  const hindiMap: Record<string, number> = {
    'एक': 1, 'दो': 2, 'तीन': 3, 'चार': 4, 'पाँच': 5, 'पांच': 5,
    'छह': 6, 'सात': 7, 'आठ': 8, 'नौ': 9, 'दस': 10,
    'बीस': 20, 'तीस': 30, 'चालीस': 40, 'पचास': 50,
    'साठ': 60, 'सत्तर': 70, 'अस्सी': 80, 'नब्बे': 90,
    'सौ': 100, 'हज़ार': 1000, 'हजार': 1000,
    'लाख': 100000, 'करोड़': 10000000,
  };

  let result = 0;
  const lower = text.toLowerCase();
  for (const [word, val] of Object.entries(hindiMap)) {
    if (lower.includes(word)) result += val;
  }
  return result > 0 ? String(result) : null;
}

// Extract locality from spoken text
function extractLocality(text: string): string {
  // Remove common filler words in Hindi/English/Bengali
  const fillers = [
    'locality', 'area', 'location', 'place', 'इलाका', 'जगह', 'क्षेत्र',
    'rate', 'रेट', 'दर', 'price', 'कीमत', 'per gaz', 'प्रति गज',
    'registered', 'unregistered', 'रजिस्टर्ड', 'अनरजिस्टर्ड',
    'is', 'hai', 'है', 'ka', 'का', 'ki', 'की', 'ke', 'के',
    'add', 'submit', 'जोड़', 'दर्ज',
  ];
  let cleaned = text;
  fillers.forEach(f => {
    cleaned = cleaned.replace(new RegExp(f, 'gi'), '');
  });
  return cleaned.trim().replace(/\s+/g, ' ');
}

// Parse voice transcript and return field updates
function parseVoiceInput(transcript: string, lang: VoiceLang): { locality?: string; rate?: string } {
  let result: { locality?: string; rate?: string } = {};
  const lower = transcript.toLowerCase();

  // Detect rate patterns
  const rateKeywords = ['rate', 'रेट', 'दर', 'price', 'कीमत', 'amount', 'rupee', 'rupees', 'rs', '₹', 'টাকা'];
  const hasRateKeyword = rateKeywords.some(k => lower.includes(k));

  // Detect locality patterns
  const localityKeywords = ['locality', 'area', 'location', 'इलाका', 'जगह', 'क्षेत्र', 'এলাকা', 'জায়গা'];
  const hasLocalityKeyword = localityKeywords.some(k => lower.includes(k));

  const extractedRate = extractRate(transcript);

  if (hasRateKeyword && extractedRate) {
    result.rate = extractedRate;
  }

  if (hasLocalityKeyword) {
    result.locality = extractLocality(transcript);
  }

  // If only a number was spoken, treat as rate
  if (!hasLocalityKeyword && !hasRateKeyword && extractedRate) {
    result.rate = extractedRate;
  }

  // If no number and no rate keyword, treat as locality
  if (!hasRateKeyword && !extractedRate && transcript.trim().length > 2) {
    result.locality = transcript.trim();
  }

  return result;
}

export default function AddRateBottomSheet({ open, onClose, pinnedLat, pinnedLng, onSearchOnMap, initialLocality, initialRate, initialLandType }: AddRateBottomSheetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Locality autocomplete state (mirrors FloatingSearchBar)
  const [localityQuery, setLocalityQuery] = useState('');
  const [localityResults, setLocalityResults] = useState<{ place_id: number; display_name: string; lat?: string; lon?: string }[]>([]);
  const [localityLoading, setLocalityLoading] = useState(false);
  const [localityFocused, setLocalityFocused] = useState(false);
  const [localityNoResults, setLocalityNoResults] = useState(false);
  const localityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedLocalityLatLng, setSelectedLocalityLatLng] = useState<{ lat: number; lng: number } | null>(null);

  // Voice state
  const [isListening, setIsListening] = useState(false);
  const [voiceLang, setVoiceLang] = useState<VoiceLang>(VOICE_LANGUAGES[0]);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [voiceSupported, setVoiceSupported] = useState(true);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const [rateUnit, setRateUnit] = useState<'per gaz' | 'per sq. ft.'>('per gaz');
  const [showRateUnitMenu, setShowRateUnitMenu] = useState(false);
  const rateUnitMenuRef = useRef<HTMLDivElement>(null);

  const hasPinnedLocation = pinnedLat != null && pinnedLng != null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AddRateFormValues>({
    defaultValues: {
      locality: '',
      rate: '',
      landType: '',
      yourName: '',
    },
  });

  const selectedLandType = watch('landType');

  // Fetch locality suggestions from Nominatim (same as FloatingSearchBar)
  const fetchLocalitySuggestions = useCallback(async (value: string) => {
    if (value.trim().length < 2) {
      setLocalityResults([]);
      setLocalityNoResults(false);
      return;
    }
    setLocalityLoading(true);
    setLocalityNoResults(false);
    try {
      const res = await fetch(`/api/geocode?q=${encodeURIComponent(value)}`);
      if (res.ok) {
        const data: { place_id: number; display_name: string; lat?: string; lon?: string }[] = await res.json();
        setLocalityResults(data);
        if (data.length === 0) setLocalityNoResults(true);
      }
    } catch {
      setLocalityResults([]);
    } finally {
      setLocalityLoading(false);
    }
  }, []);

  const handleLocalityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalityQuery(value);
    setValue('locality', value, { shouldValidate: value.length > 0 });
    setLocalityNoResults(false);
    setSelectedLocalityLatLng(null);
    if (localityDebounceRef.current) clearTimeout(localityDebounceRef.current);
    localityDebounceRef.current = setTimeout(() => fetchLocalitySuggestions(value), 350);
  };

  const handleLocalitySelect = (result: { display_name: string; lat?: string; lon?: string }) => {
    const name = result.display_name.split(',')[0].trim();
    setLocalityQuery(name);
    setValue('locality', name, { shouldValidate: true });
    setLocalityResults([]);
    setLocalityFocused(false);
    setLocalityNoResults(false);
    if (result.lat && result.lon) {
      setSelectedLocalityLatLng({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) });
    }
  };

  const showLocalityDropdown = localityFocused && localityQuery.length >= 2 && (localityResults.length > 0 || localityLoading || localityNoResults);

  // Close rate unit dropdown on outside click
  useEffect(() => {
    if (!showRateUnitMenu) return;
    function handleClickOutside(e: MouseEvent) {
      if (rateUnitMenuRef.current && !rateUnitMenuRef.current.contains(e.target as Node)) {
        setShowRateUnitMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRateUnitMenu]);

// Check speech recognition support
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognitionAPI =
        (window as typeof window & { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).SpeechRecognition ||
        (window as typeof window & { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).webkitSpeechRecognition;
      if (!SpeechRecognitionAPI) {
        setVoiceSupported(false);
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      setIsAnimatingOut(false);
      setSubmitted(false);
      setIsVisible(true);
      // Pre-fill locality if provided (from Search on Map flow or Correct Rate)
      if (initialLocality) {
        setLocalityQuery(initialLocality);
        setValue('locality', initialLocality, { shouldValidate: false });
      }
      if (initialRate) {
        setValue('rate', initialRate, { shouldValidate: false });
      }
      if (initialLandType) {
        setValue('landType', initialLandType, { shouldValidate: false });
      }
      document.body.style.overflow = 'hidden';
    }
  }, [open]);

  // Close lang menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!voiceSupported) return;
    setVoiceError('');
    setVoiceTranscript('');

    const SpeechRecognitionAPI =
      (window as typeof window & { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).SpeechRecognition ||
      (window as typeof window & { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) return;

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = voiceLang.actualCode ?? voiceLang.code;
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      const transcript = final || interim;
      setVoiceTranscript(transcript);

      if (final) {
        const parsed = parseVoiceInput(final, voiceLang);
        if (parsed.locality) setValue('locality', parsed.locality, { shouldValidate: true });
        if (parsed.rate) setValue('rate', parsed.rate, { shouldValidate: true });
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const msgMap: Record<string, string> = {
        'not-allowed': 'Microphone access denied. Please allow mic permission.',
        'no-speech': 'No speech detected. Try again.',
        'network': 'Network error. Check your connection.',
        'audio-capture': 'No microphone found.',
      };
      setVoiceError(msgMap[event.error] || 'Voice input failed. Try again.');
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [voiceLang, voiceSupported, setValue]);

  const toggleListening = () => {
    if (isListening) stopListening();
    else startListening();
  };

  const handleClose = () => {
    stopListening();
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
      document.body.style.overflow = '';
      reset();
      setLocalityQuery('');
      setLocalityResults([]);
      setLocalityNoResults(false);
      setSelectedLocalityLatLng(null);
      setVoiceTranscript('');
      setVoiceError('');
      onClose();
    }, 220);
  };

  const handleSearchOnMap = () => {
    stopListening();
    setIsAnimatingOut(true);
    const currentQuery = localityQuery;
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
      document.body.style.overflow = '';
      reset();
      setLocalityQuery('');
      setLocalityResults([]);
      setLocalityNoResults(false);
      setSelectedLocalityLatLng(null);
      setVoiceTranscript('');
      setVoiceError('');
      onSearchOnMap?.(currentQuery);
    }, 220);
  };

  const onSubmit = async (data: AddRateFormValues) => {
    setIsSubmitting(true);

    // Resolve coordinates: prefer pinned location, then locality centroid
    const resolvedLat = pinnedLat ?? selectedLocalityLatLng?.lat ?? null;
    const resolvedLng = pinnedLng ?? selectedLocalityLatLng?.lng ?? null;

    // Optimistic update: push a temporary rate to the map immediately
    const tempId = `temp-${Date.now()}`;
    const hasCoords = resolvedLat != null && resolvedLng != null;

    if (hasCoords && typeof window !== 'undefined' && (window as any).__addLiveRate) {
      (window as any).__addLiveRate({
        id: tempId,
        locality: data.locality,
        rate: Number(data.rate),
        landType: data.landType as 'Registered' | 'Unregistered',
        lat: resolvedLat,
        lng: resolvedLng,
        createdAt: new Date().toISOString(),
      });
    }

    try {
      const newRate = await landRatesService.create({
        locality: data.locality,
        rate: Number(data.rate),
        landType: data.landType as 'Registered' | 'Unregistered',
        lat: resolvedLat,
        lng: resolvedLng,
      });
      // Replace the temp marker with the real one (or just add if no coords)
      if (newRate && typeof window !== 'undefined') {
        if ((window as any).__replaceLiveRate) {
          (window as any).__replaceLiveRate(tempId, newRate);
        } else if ((window as any).__addLiveRate) {
          // Fallback: remove temp and add real
          if ((window as any).__removeLiveRate) (window as any).__removeLiveRate(tempId);
          (window as any).__addLiveRate(newRate);
        }
      }
    } catch (err) {
      console.error('Failed to submit rate:', err);
      // Remove optimistic marker on failure
      if (hasCoords && typeof window !== 'undefined' && (window as any).__removeLiveRate) {
        (window as any).__removeLiveRate(tempId);
      }
    }
    setIsSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      handleClose();
    }, 1800);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-end justify-center overlay-enter"
      aria-modal="true"
      role="dialog"
      aria-label="Add land rate"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bottom-sheet-overlay"
        onClick={handleClose}
      />

      {/* Sheet */}
      <div
        className={`
          relative w-full max-w-lg bg-card rounded-t-3xl shadow-[0_-8px_40px_rgba(0,0,0,0.14)]
          ${isAnimatingOut ? 'bottom-sheet-exit' : 'bottom-sheet-enter'}
        `}
        style={{ 
          maxHeight: '92dvh',
          overflowY: 'auto',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-border rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-4 border-b border-border">
          <div>
            <h2 className="text-base font-bold text-foreground">Add Land Rate</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {hasPinnedLocation ? 'Rate for your pinned location' : 'Help the community with accurate rates'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Voice input controls */}
            {voiceSupported && (
              <div className="flex items-center gap-1.5">
                {/* Language selector */}
                <div className="relative" ref={langMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowLangMenu(v => !v)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-muted border border-border hover:bg-border transition-colors duration-150 text-xs font-semibold text-foreground"
                    aria-label="Select voice language"
                  >
                    <span>{voiceLang.short}</span>
                    <ChevronDown size={11} className="text-muted-foreground" />
                  </button>
                  {showLangMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-float z-[2100] min-w-[130px] overflow-hidden">
                      {VOICE_LANGUAGES.map((lang) => (
                        <button
                          key={lang.code + lang.label}
                          type="button"
                          onClick={() => {
                            setVoiceLang(lang);
                            setShowLangMenu(false);
                            if (isListening) stopListening();
                          }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium text-left transition-colors duration-100 border-b border-border last:border-b-0
                            ${voiceLang.label === lang.label ? 'bg-secondary text-primary' : 'text-foreground hover:bg-muted'}`}
                        >
                          <span className="w-6 text-center font-bold text-[10px] text-muted-foreground">{lang.short}</span>
                          <span>{lang.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mic button */}
                <button
                  type="button"
                  onClick={toggleListening}
                  aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                  className={`
                    w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200
                    ${isListening
                      ? 'bg-red-500 text-white shadow-[0_0_0_4px_rgba(239,68,68,0.20)] animate-pulse'
                      : 'bg-primary text-primary-foreground hover:bg-accent shadow-fab'
                    }
                  `}
                >
                  {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
              </div>
            )}

            <button
              onClick={handleClose}
              aria-label="Close"
              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-border transition-colors duration-150"
            >
              <X size={16} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {submitted ? (
          /* Success state */
          <div className="flex flex-col items-center justify-center py-12 px-5 gap-4">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
              <CheckCircle2 size={34} className="text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-bold text-foreground">Rate Submitted!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Your rate has been added to the map. Thank you for contributing.
              </p>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit(onSubmit)} className="px-5 pt-5 pb-8 flex flex-col gap-5">

            {/* Voice status banner */}
            {(isListening || voiceTranscript || voiceError) && (
              <div className={`flex items-start gap-3 rounded-xl px-4 py-3 border transition-all duration-200
                ${voiceError
                  ? 'bg-red-50 border-red-200'
                  : isListening
                    ? 'bg-blue-50 border-blue-200' :'bg-green-50 border-green-200'
                }`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                  ${voiceError ? 'bg-red-100' : isListening ? 'bg-primary' : 'bg-green-100'}`}
                >
                  {isListening
                    ? <Mic size={13} className="text-white" />
                    : voiceError
                      ? <MicOff size={13} className="text-red-500" />
                      : <Mic size={13} className="text-green-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  {voiceError ? (
                    <p className="text-xs font-medium text-red-600">{voiceError}</p>
                  ) : isListening ? (
                    <>
                      <p className="text-xs font-semibold text-primary">
                        Listening in {voiceLang.label}…
                      </p>
                      {voiceTranscript && (
                        <p className="text-xs text-blue-700 mt-0.5 italic truncate">"{voiceTranscript}"</p>
                      )}
                    </>
                  ) : voiceTranscript ? (
                    <>
                      <p className="text-xs font-semibold text-green-700">Voice input applied</p>
                      <p className="text-xs text-green-600 mt-0.5 italic truncate">"{voiceTranscript}"</p>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {/* Pinned location badge — shown only when long-press triggered */}
            {hasPinnedLocation && (
              <div className="flex items-center gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Navigation size={13} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-primary">Pinned Location</p>
                  <p className="text-xs text-blue-700 font-mono mt-0.5 truncate">
                    {pinnedLat!.toFixed(5)}, {pinnedLng!.toFixed(5)}
                  </p>
                </div>
              </div>
            )}

            {/* Locality field */}
            <div className="flex flex-col gap-1.5 relative">
              <label htmlFor="locality" className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <MapPin size={13} className="text-primary" />
                Locality
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="locality"
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. Uttam Nagar, Dwarka Sec 12…"
                  value={localityQuery}
                  onChange={handleLocalityChange}
                  onFocus={() => setLocalityFocused(true)}
                  onBlur={() => setTimeout(() => setLocalityFocused(false), 200)}
                  className={`
                    w-full px-4 py-3 rounded-xl border text-sm font-medium text-foreground
                    placeholder:text-muted-foreground bg-muted
                    transition-all duration-150 input-focus-ring
                    ${errors.locality ? 'border-red-400 bg-red-50' : 'border-border'}
                  `}
                />
                {/* Hidden input to keep react-hook-form validation working */}
                <input type="hidden" {...register('locality', { required: 'Locality is required', minLength: { value: 3, message: 'Enter at least 3 characters' } })} />
                {showLocalityDropdown && (
                  <div className="absolute top-full mt-1 left-0 right-0 bg-card rounded-xl shadow-float border border-border z-50 overflow-hidden">
                    {localityLoading && localityResults.length === 0 && (
                      <div className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        Searching…
                      </div>
                    )}
                    {!localityLoading && localityNoResults && (
                      <div className="px-4 py-3 flex flex-col gap-2">
                        <p className="text-xs text-red-500 font-medium flex items-center gap-1.5">
                          <span>⚠️</span>
                          No results found for &quot;{localityQuery}&quot;
                        </p>
                        {onSearchOnMap && (
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setLocalityFocused(false);
                              handleSearchOnMap();
                            }}
                            className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-accent transition-colors duration-150"
                          >
                            <MapPin size={13} />
                            Search on Map
                          </button>
                        )}
                      </div>
                    )}
                    {localityResults.map((result) => {
                      const parts = result.display_name.split(',');
                      const primary = parts[0]?.trim();
                      const secondary = parts.slice(1, 3).join(',').trim();
                      return (
                        <button
                          key={result.place_id}
                          type="button"
                          onMouseDown={() => handleLocalitySelect(result)}
                          className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-muted text-sm font-medium text-foreground text-left border-b border-border last:border-b-0 transition-colors duration-100"
                        >
                          <MapPin size={12} className="text-muted-foreground flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">{primary}</div>
                            {secondary && <div className="text-xs text-muted-foreground truncate">{secondary}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {errors.locality && (
                <p className="text-xs text-red-500 font-medium">{errors.locality.message}</p>
              )}
            </div>

            {/* Rate field */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="rate" className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <IndianRupee size={13} className="text-primary" />
                Current Market Rate (₹)
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">₹</span>
                <input
                  id="rate"
                  type="number"
                  placeholder="32500"
                  {...register('rate', {
                    required: 'Rate is required',
                    min: { value: 1000, message: 'Rate must be at least ₹1,000/gaz' },
                    max: { value: 5000000, message: 'Rate seems too high — double-check' },
                  })}
                  style={{ MozAppearance: 'textfield' } as React.CSSProperties}
                  className={`
                    w-full pl-8 pr-32 py-3 rounded-xl border text-sm font-medium text-foreground
                    placeholder:text-muted-foreground bg-muted
                    transition-all duration-150 input-focus-ring
                    [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                    ${errors.rate ? 'border-red-400 bg-red-50' : 'border-border'}
                  `}
                />
                {/* Rate unit dropdown */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2" ref={rateUnitMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowRateUnitMenu((v) => !v)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 text-gray-500 text-xs font-semibold hover:bg-primary/20 transition-colors whitespace-nowrap"
                  >
                    {rateUnit}
                    <ChevronDown size={12} className={`transition-transform ${showRateUnitMenu ? 'rotate-180' : ''}`} />
                  </button>
                  {showRateUnitMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-float z-50 overflow-hidden min-w-[110px]">
                      {(['per gaz', 'per sq. ft.'] as const).map((unit) => (
                        <button
                          key={unit}
                          type="button"
                          onClick={() => { setRateUnit(unit); setShowRateUnitMenu(false); }}
                          className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:bg-muted ${rateUnit === unit ? 'text-primary bg-secondary' : 'text-foreground'}`}
                        >
                          {unit}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {errors.rate && (
                <p className="text-xs text-red-500 font-medium">{errors.rate.message}</p>
              )}
            </div>

            {/* Land Type field */}
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <FileText size={13} className="text-primary" />
                Land Type
                <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {LAND_TYPES.map((lt) => (
                  <button
                    key={`lt-${lt.value}`}
                    type="button"
                    onClick={() => setValue('landType', lt.value, { shouldValidate: true })}
                    className={`
                      flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-150
                      ${selectedLandType === lt.value
                        ? 'border-primary bg-secondary shadow-[0_0_0_3px_rgba(0,86,210,0.10)]'
                        : 'border-border bg-muted hover:border-primary/40 hover:bg-secondary/50'
                      }
                    `}
                  >
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${lt.badgeClass}`}>
                      {lt.label}
                    </span>
                  </button>
                ))}
              </div>
              <input
                type="hidden"
                {...register('landType', { required: 'Please select a land type' })}
              />
              {errors.landType && (
                <p className="text-xs text-red-500 font-medium">{errors.landType.message}</p>
              )}
            </div>

            {/* Voice hint */}
            {voiceSupported && !isListening && !voiceTranscript && (
              <></>
            )}

            {/* Your Name field */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="yourName" className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                Your Name
                <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
              </label>
              <input
                id="yourName"
                type="text"
                autoComplete="name"
                placeholder="e.g. Rahul Sharma"
                {...register('yourName')}
                className="w-full px-4 py-3 rounded-xl border text-sm font-medium text-foreground placeholder:text-muted-foreground bg-muted transition-all duration-150 input-focus-ring border-border"
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="
                w-full bg-primary text-primary-foreground rounded-xl py-3.5
                font-semibold text-sm tracking-wide
                flex items-center justify-center gap-2
                hover:bg-accent transition-all duration-150
                active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed
                shadow-fab
              "
              style={{ minHeight: '50px' }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin" />
                  <span>Submitting…</span>
                </>
              ) : (
                'Submit Rate'
              )}
            </button>

            {/* Long press hint */}
            <p className="text-center text-xs text-muted-foreground -mt-2">
              💡 Long press on the map to add a new rate
            </p>
          </form>
        )}
      </div>
    </div>
  );
}